---
title: The session image
lede: The machine every micro-VM session boots from, written as a Dockerfile you can edit.
order: 36
part: Shape the machine
---

Every micro-VM session starts from one image, written as a Dockerfile. Edit it in **Settings › Sandbox › Session image**, and give a workspace its own in **Workspace settings › Sandbox**. A session placed on this computer builds no image and uses your own toolchain.

![The session image](media/settings-sandbox.webp)

### Edit it

The default image is Debian 13 (trixie) with Node 26, git, tmux, Python 3 and the Claude Code and Codex CLIs. Only `FROM`, `RUN`, `ENV` and `ARG` apply.

After a change, the next session rebuilds the image, so **that one starts slowly**. A Dockerfile that does not parse cannot be saved, and the page points at the line. **Restore default** puts back the image Airlock ships.

:::warn Keep the agents
Removing `claude` from the image stops sessions from starting. Codex sessions need `codex`.
:::

### A workspace's own image

All the repositories of a workspace share one VM, so its image must carry every toolchain they need. Press **Override** on the **Session image** row, then:

- **Generate** reads the versions your repositories pin and writes a layer for each toolchain. What it could not settle is listed under the editor.
- **Use app image** starts again from the application's image.

The text is yours afterwards. When you add, remove or repoint a repository, Airlock regenerates only its own layers.

![A workspace's session image](media/ws-sandbox.webp)

| Toolchain | Read from |
|---|---|
| Node, pnpm, Yarn, Bun | `.nvmrc`, `.node-version`, `package.json`, pnpm files |
| Java, Maven, Gradle | `pom.xml`, the Gradle build, `.java-version`, `.sdkmanrc` |
| Python, uv | `.python-version`, else `requires-python` in `pyproject.toml` |
| Go | `go.mod` |
| Rust | `rust-toolchain.toml` or `rust-toolchain`, else stable |
| PHP, Composer | `.php-version`, then `composer.json` |

PHP is version-aware: 8.1 to 8.5, the highest any repository asks for, with the extensions a Laravel, Symfony or Slim app needs. A repository that names no version gets Debian's 8.4.

Python is too: the version a `.python-version` names (the highest across repositories), or one that fits every `requires-python`. Debian's 3.13 serves when it fits; any other is installed by uv from GitHub. If that download fails, the image keeps Debian's Python.

When a layer downloads from a host the workspace does not allow, **Allow it** adds that host to its [network](/network) list.

### VM resources

| Setting | Default | Range |
|---|---|---|
| **CPU cores** | 6 | 1–16 |
| **Memory** | 12,288 MB | 1,024–65,536 MB |
| **Disk** | 10 GB | 5–500 GB |

They are ceilings, not reservations, and a workspace can override each. A session starts with 3,072 MB and grows towards **Memory**. On any computer each is held to half of its cores, half of its memory and half of its free disk, and the page says the number. Changes apply to sessions started afterwards. **Container runtime** turns on [Docker in the VM](/docker).

**Largest repository history** (512 MB by default, 64–8,192 MB, app-wide) caps the git bundle a session carries for each repository. A bigger one stops the session from starting, and names the repository.

### Warm images

A warm image already carries a workspace's installed dependencies, so a session boots with nothing to download. When what it restored matches the project's lockfiles and build files, the project's install command is skipped altogether (see **Skip when dependencies are unchanged** in [Repositories](/repositories)); a project whose install failed when the image was built still installs, without costing the others their shared cache.

A Java project runs on the JDK its Gradle wrapper can run on: the version the repository pins when its Gradle supports it, else the newest JDK of the image that it does. A `JAVA_HOME` the project or organization sets is never overridden. **Images kept** (5 by default, 1–20, app-wide) sets how many Airlock keeps: the newest of each workspace first, then the most recently used.

### Where sessions run

**Backend**, at the top of **Settings › Sandbox**, picks **Micro-VM when available (recommended)** or **Local — no isolation** for new sessions; a workspace sets its own with **Sessions run in**. See [Where a session runs](/placement).
